import React from 'react';
import { Shield, CheckCircle, Award, FileCheck, Eye, Thermometer } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Quality = () => {
  const qualitySteps = [
    {
      icon: Eye,
      title: 'Farm-Level Inspection',
      description: 'Direct sourcing from verified farms with initial quality assessment at harvest'
    },
    {
      icon: Shield,
      title: 'Grading & Selection',
      description: 'Products sorted and graded according to size, appearance, and quality standards'
    },
    {
      icon: Thermometer,
      title: 'Storage Conditions',
      description: 'Proper storage in controlled environments to maintain freshness before shipping'
    },
    {
      icon: FileCheck,
      title: 'Pre-Export Inspection',
      description: 'Final quality check before container loading with documentation'
    },
    {
      icon: Award,
      title: 'Compliance Verification',
      description: 'Ensuring all products meet international food safety and export standards'
    },
    {
      icon: CheckCircle,
      title: 'Container Monitoring',
      description: 'Temperature and condition monitoring during transit (reefer containers)'
    }
  ];

  const certifications = [
    {
      title: 'Phytosanitary Certification',
      description: 'All shipments certified by Nigerian Plant Quarantine Service',
      icon: Award
    },
    {
      title: 'Quality Inspection Reports',
      description: 'Third-party quality inspection when required by clients',
      icon: FileCheck
    },
    {
      title: 'Food Safety Compliance',
      description: 'Adherence to international food safety standards',
      icon: Shield
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1673732280286-bde8e813ad6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxxdWFsaXR5JTIwaW5zcGVjdGlvbnxlbnwwfHx8fDE3NzEyODAxNjJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Quality Assurance"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1B5E20]/80"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Quality Assurance</h1>
          <p className="text-xl text-white/95 max-w-3xl mx-auto">
            Multi-stage quality control ensuring only premium products reach our clients
          </p>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold text-[#333333] mb-6">Our Quality Commitment</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              At Folalabi Farms, quality is not an afterthought—it's built into every stage of our operations. From farm selection to final delivery, we implement rigorous quality control measures to ensure our clients receive only the best African agricultural products.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {qualitySteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-[#C8A951] transition-all hover:shadow-lg bg-white">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-full flex items-center justify-center mb-4 mx-auto">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold text-[#333333] mb-3 text-center">{step.title}</h3>
                    <p className="text-gray-600 text-center">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Quality Standards</h2>
            <p className="text-lg text-gray-600">
              We adhere to strict quality parameters for all our products
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-6">
            {[
              {
                category: 'Yams',
                standards: [
                  'Size: Minimum 1kg per tuber',
                  'Appearance: Clean, firm, free from cuts and bruises',
                  'Freshness: Harvested within 48 hours of export processing',
                  'Sorting: Graded by size and quality (Premium, Grade A, Grade B)',
                  'Packaging: Clean polypropylene bags with proper ventilation'
                ]
              },
              {
                category: 'Cassava Leaves',
                standards: [
                  'Color: Fresh green with no yellowing or wilting',
                  'Freshness: Cut and processed within 24 hours',
                  'Cleanliness: Thoroughly washed and inspected',
                  'Temperature: Maintained at 2-4°C during storage and transit',
                  'Packaging: Vacuum-sealed bags for extended freshness'
                ]
              },
              {
                category: 'Palm Oil',
                standards: [
                  'Purity: 100% pure red palm oil',
                  'Processing: Traditional extraction methods',
                  'Quality: Free from adulterants and contaminants',
                  'Testing: Lab analysis for quality parameters',
                  'Packaging: Food-grade containers with proper sealing'
                ]
              }
            ].map((product, index) => (
              <Card key={index} className="border-0 shadow-lg bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#C8A951] rounded-full flex items-center justify-center text-white font-bold">
                      {index + 1}
                    </div>
                    {product.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {product.standards.map((standard, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="text-[#1B5E20] flex-shrink-0 mt-1" size={18} />
                        <span className="text-gray-700">{standard}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Certifications & Compliance</h2>
            <p className="text-lg text-gray-600">
              Full compliance with international export and food safety requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {certifications.map((cert, index) => {
              const IconComponent = cert.icon;
              return (
                <Card key={index} className="border-0 shadow-lg bg-gradient-to-br from-[#F9F9F9] to-white hover:shadow-xl transition-all">
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-white" size={36} />
                    </div>
                    <h3 className="text-xl font-bold text-[#333333] mb-3">{cert.title}</h3>
                    <p className="text-gray-600">{cert.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quality Guarantee */}
      <section className="py-20 bg-gradient-to-r from-[#1B5E20] to-[#2E7D32] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="text-white" size={40} />
              </div>
              <h2 className="text-4xl font-bold mb-6">Our Quality Guarantee</h2>
            </div>

            <Card className="border-0 bg-white/10 backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="text-[#C8A951] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Fresh Products</h3>
                      <p className="text-white/90">All products are freshly harvested and processed shortly before export to ensure maximum freshness upon arrival.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="text-[#C8A951] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Proper Handling</h3>
                      <p className="text-white/90">Temperature-controlled storage and shipping (where required) to maintain product quality throughout the supply chain.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="text-[#C8A951] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Transparent Documentation</h3>
                      <p className="text-white/90">Complete documentation and traceability from source to destination for full transparency.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <CheckCircle className="text-[#C8A951] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="text-xl font-semibold mb-2">Client Support</h3>
                      <p className="text-white/90">Our team is available to address any quality concerns and ensure complete satisfaction.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quality;
